import { lowerCase } from 'lodash-es'
import {
  InboxIcon,
  AlertCircleIcon,
  ListIcon,
  MessageCircleIcon,
  SmileIcon,
  ThumbsUpIcon,
  UserCheckIcon,
} from '@klausapp/ui-kit/icons'

import { session } from '@/composables/useSession'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import SendIcon from '@/assets/send-icon.svg'
import i18n from '@/i18n'
import type { DisputeListStatus, CommentSource, ReactionSource } from '../types'

export type DisputeStatuses = Record<DisputeListStatus, string>

export type CommentStatuses = Record<'ALL' | 'MENTIONS', string>

export type CommentSourceStatuses = Record<CommentSource, string>

export type ReactionSourceStatuses = Record<ReactionSource, string>

export type FeedbackStatuses = Record<'ALL' | 'CSAT', string>

interface ActivityStatuses {
  disputes: DisputeStatuses
  comments: CommentStatuses
  commentsSource: CommentSourceStatuses
  reactionsSource: ReactionSourceStatuses
  feedback: FeedbackStatuses
}

export const useStatuses = (): ActivityStatuses => {
  return {
    disputes: {
      ALL: i18n.t('pickers.all'),
      NEW: i18n.t('pickers.open'),
      CLOSED: i18n.t('pickers.resolved'),
      ACCEPTED: i18n.t('pickers.accepted'),
      PARTIALLY_ACCEPTED: i18n.t('pickers.partially_accepted'),
      REJECTED: i18n.t('pickers.rejected'),
    },
    comments: {
      ALL: i18n.t('pickers.all'),
      MENTIONS: i18n.t('pickers.mentions'),
    },
    commentsSource: {
      ALL: i18n.t('activity.pickers.reviews_and_evaluations'),
      REVIEWS: i18n.t('activity.subnav.reviews'),
      EVALUATIONS: i18n.t('activity.subnav.evaluations'),
    },
    reactionsSource: {
      ALL: i18n.t('activity.pickers.reviews_and_evaluations'),
      REVIEWS: i18n.t('activity.subnav.reviews'),
      EVALUATIONS: i18n.t('activity.subnav.evaluations'),
    },
    feedback: {
      ALL: i18n.t('pickers.all'),
      CSAT: i18n.t('universal.csat'),
    },
  }
}

export enum SortValues {
  NEWEST = '-createdAt',
  OLDEST = 'createdAt',
  UNREAD = 'unread',
  COMMENTED = 'commented',
}

const defaultOptions = {
  'conversations.ticket_list.sort_newest': SortValues.NEWEST,
  'conversations.ticket_list.sort_oldest': SortValues.OLDEST,
}
const receivedOptions = {
  ...defaultOptions,
  'conversations.ticket_list.sort_unread': SortValues.UNREAD,
}
const givenReviewOptions = {
  ...defaultOptions,
  'conversations.ticket_list.sort_commented': SortValues.COMMENTED,
}
const receivedReviewOptions = {
  ...receivedOptions,
  'conversations.ticket_list.sort_commented': SortValues.COMMENTED,
}

export const sortOptions = {
  commentsReceived: receivedOptions,
  commentsGiven: defaultOptions,
  reviewsReceived: receivedReviewOptions,
  reviewsGiven: givenReviewOptions,
  feedbackReceived: defaultOptions,
  disputesAll: receivedOptions,
  disputesReceived: receivedOptions,
  disputesStarted: defaultOptions,
  // TODO: check which sort options should be added
  evaluationsReceived: receivedOptions,
  evaluationsGiven: givenReviewOptions,
} as const

export const getActivityRoutes = () => {
  const isZendeskUi = session.features.zendeskUiNavigation

  return [
    {
      value: 'placeholder',
      label: isZendeskUi ? i18n.t('activity.subnav.received') : i18n.t('activity.subnav.received_uppercase'),
    },
    {
      value: 'activity.reviewsReceived',
      icon: !isZendeskUi && ListIcon,
      label: i18n.t('activity.subnav.reviews'),
    },
    ...(!hasWorkspaceRole('AGENT') && session.features.gradeTheReviewerDev
      ? [
          {
            value: 'activity.evaluationsReceived',
            icon: !isZendeskUi && UserCheckIcon,
            label: i18n.t('activity.subnav.evaluations'),
          },
        ]
      : []),
    {
      value: 'activity.commentsReceived',
      icon: !isZendeskUi && MessageCircleIcon,
      label: i18n.t('activity.subnav.comments'),
    },
    {
      value: 'activity.reactionsReceived',
      icon: !isZendeskUi && SmileIcon,
      label: i18n.t('activity.subnav.reactions'),
    },
    {
      value: 'activity.feedbackReceived',
      icon: !isZendeskUi && ThumbsUpIcon,
      label: i18n.t('activity.subnav.feedback'),
    },
    {
      value: 'placeholder',
      label: isZendeskUi ? i18n.t('activity.subnav.given') : i18n.t('activity.subnav.given_uppercase'),
    },
    ...(!hasWorkspaceRole('AGENT') || session.workspace?.settings?.selfReviews
      ? [
          {
            value: 'activity.reviewsGiven',
            icon: !isZendeskUi && ListIcon,
            label: i18n.t('activity.subnav.reviews'),
          },
        ]
      : []),
    ...(hasWorkspaceRole('MANAGER', 'LEAD') && session.features.gradeTheReviewerDev
      ? [
          {
            value: 'activity.evaluationsGiven',
            icon: !isZendeskUi && UserCheckIcon,
            label: i18n.t('activity.subnav.evaluations'),
          },
        ]
      : []),
    {
      value: 'activity.commentsGiven',
      icon: !isZendeskUi && MessageCircleIcon,
      label: i18n.t('activity.subnav.comments'),
    },
    {
      value: 'placeholder',
      label: isZendeskUi ? i18n.t('activity.subnav.disputes') : i18n.t('activity.subnav.disputes_uppercase'),
    },
    ...(session.features.disputes
      ? [
          {
            value: 'activity.disputesAll',
            icon: !isZendeskUi && AlertCircleIcon,
            label: i18n.t('activity.subnav.all'),
          },
          ...(!hasWorkspaceRole('AGENT')
            ? [
                {
                  value: 'activity.disputesReceived',
                  icon: !isZendeskUi && InboxIcon,
                  label: i18n.t('activity.subnav.received'),
                },
              ]
            : []),
          {
            value: 'activity.disputesStarted',
            icon: !isZendeskUi && SendIcon,
            label: i18n.t('activity.subnav.started'),
          },
        ]
      : []),
  ]
}

export const getRouteSuffix = (routeName: string) => {
  const name = routeName.startsWith('activity.') ? routeName.replace('activity.', '') : routeName
  return name
}

export const getDotNotation = (routeName: string) => {
  return lowerCase(getRouteSuffix(routeName)).replace(/ /g, '.')
}
